import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { resolveCoinMedia } from '../../utils/coinMedia';
import styles from './CoinMedia.module.css';

/**
 * Renders a coin's artwork.
 *
 * - Static coins: a plain <img>, exactly as before.
 * - Signature (animated) coins:
 *     animate=false (grids, lists, feed, admin tables) → static poster only
 *     animate=true  (detail, unlock, public coin page)  → muted looping video,
 *       with the poster shown while it loads and whenever video can't play.
 * - prefers-reduced-motion always gets the static poster.
 */
export default function CoinMedia({
    coin,
    fallbackUrl = null,
    animate = false,
    alt = 'Coin',
    className = '',
    imgProps = {},
    fallback = null,
}) {
    const media = resolveCoinMedia(coin, fallbackUrl);
    const reducedMotion = useReducedMotion();
    const [videoFailed, setVideoFailed] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => { setVideoFailed(false); }, [media.animationUrl]);
    useEffect(() => { setImageFailed(false); }, [media.posterUrl]);

    const playVideo = animate && media.type === 'ANIMATED' && !reducedMotion && !videoFailed;

    // Some browsers only honour `muted` for autoplay when set as a property.
    useEffect(() => {
        const el = videoRef.current;
        if (!playVideo || !el) return;
        el.muted = true;
        const attempt = el.play?.();
        if (attempt?.catch) attempt.catch(() => {}); // autoplay blocked → poster stays visible
    }, [playVideo, media.animationUrl]);

    // A user's Signature coin: the still, ringed coin (name / COLLECTION / date)
    // is always the base layer and the animation plays inside its centre, so an
    // awarded animated coin keeps the same ring as every other coin.
    const inset = Number(media.animationInset) > 0 && Number(media.animationInset) < 1 ? Number(media.animationInset) : 1;
    const framed = inset < 1 && Boolean(media.posterUrl) && !imageFailed;

    const video = playVideo ? (
        <video
            ref={videoRef}
            className={framed ? styles.framedVideo : `${styles.media} ${className}`}
            style={framed ? {
                top: `${((1 - inset) / 2) * 100}%`,
                left: `${((1 - inset) / 2) * 100}%`,
                width: `${inset * 100}%`,
                height: `${inset * 100}%`,
            } : undefined}
            src={media.animationUrl}
            poster={(framed ? media.artPosterUrl : media.posterUrl) || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            disablePictureInPicture
            aria-label={alt}
            data-testid="coin-media-video"
            onError={() => setVideoFailed(true)}
        />
    ) : null;

    if (playVideo && framed) {
        return (
            <span className={`${styles.framed} ${className}`} data-testid="coin-media-framed">
                <img
                    className={styles.framedBase}
                    src={media.posterUrl}
                    alt={alt}
                    data-testid="coin-media-image"
                    onError={() => setImageFailed(true)}
                />
                {video}
            </span>
        );
    }

    if (playVideo) return video;

    if (!media.posterUrl || imageFailed) return fallback;

    return (
        <img
            className={`${styles.media} ${className}`}
            src={media.posterUrl}
            alt={alt}
            data-testid="coin-media-image"
            onError={() => setImageFailed(true)}
            {...imgProps}
        />
    );
}

/** Small marker for Signature coins wherever only the static poster is shown. */
export function SignatureBadge({ coin, compact = false, className = '' }) {
    if (resolveCoinMedia(coin).type !== 'ANIMATED') return null;
    return (
        <span
            className={`${styles.signatureBadge} ${compact ? styles.signatureBadgeCompact : ''} ${className}`}
            title="Signature Coin — animated"
            data-testid="signature-badge"
        >
            <span aria-hidden="true">💎</span>
            {compact ? <span className={styles.srOnly}>Signature Coin</span> : <span>Signature</span>}
        </span>
    );
}
